import React, { useState } from 'react';
import { useWidgetEdit } from '../hooks/useWidgetEdit';
import './EventInfoWidgetEdit.css';
import './TopWidgetEdit.css';
import { useUserEvents } from '../hooks/useEventData';
import TextSettingsSection from '../WidgetEditComponents/TextSettingsSection';
import EventFeaturesManagement from '../WidgetEditComponents/EventFeaturesManagement';
import AdvancedSettingsSection from '../WidgetEditComponents/AdvancedSettingsSection';
import ImageContainer from '../WidgetEditComponents/ImageContainer';
import MoreSettingsSection from '../WidgetEditComponents/MoreSettingsSection';
import ToggleSwitch from '../WidgetEditComponents/ToggleSwitch';

function EventInfoWidgetEdit({ widget, onUpdate, colors }) {
    const { editData, handleInputChange, uploadImage, handleSliderChange } = useWidgetEdit(widget, onUpdate);
    const { events, loading: eventsLoading } = useUserEvents();
    
    // Upload States
    const [isUploadingEvent, setIsUploadingEvent] = useState(false);
    const [isUploadingBackground, setIsUploadingBackground] = useState(false);

    // Event Selection Handler
    const handleEventSelection = (eventId) => {
        handleInputChange({ target: { name: 'eventId', value: eventId } });
    };

    // 🎫 Event Features Management - New Toggle Section
    const addFeature = () => {
        const currentFeatures = editData.customFeatures || [];
        const newFeature = {
            id: `feature${Date.now()}`,
            text: 'New event feature'
        };
        const updatedFeatures = [...currentFeatures, newFeature];
        handleInputChange({ target: { name: 'customFeatures', value: updatedFeatures } });
    };

    const removeFeature = (index) => {
        const currentFeatures = editData.customFeatures || [];
        const updatedFeatures = currentFeatures.filter((_, i) => i !== index);
        handleInputChange({ target: { name: 'customFeatures', value: updatedFeatures } });
    };

    const updateFeature = (index, text) => {
        const currentFeatures = editData.customFeatures || [];
        const updatedFeatures = [...currentFeatures];
        updatedFeatures[index] = { ...updatedFeatures[index], text };
        handleInputChange({ target: { name: 'customFeatures', value: updatedFeatures } });
    };


    // Selected event data
    const selectedEvent = events.find(event => event.id === editData.eventId);

    // 🚀 REVOLUTIONARY TEXT SETTINGS - Event Information Text Configuration
    const textConfig = [
        // Section Titles
        {
            fieldName: 'scheduleTitle',
            label: 'Event Schedule Title',
            type: 'input',
            placeholder: 'Event Schedule',
            fontField: 'scheduleTitleFont',
            fontType: 'big',
            effectField: 'scheduleTitleEffect',
            showToggleField: 'showScheduleTitle'
        },
        {
            fieldName: 'featuresTitle',
            label: 'Features Section Title',
            type: 'input',
            placeholder: "What's Included",
            fontField: 'featuresTitleFont',
            fontType: 'big',
            effectField: 'featuresTitleEffect',
            showToggleField: 'showFeaturesTitle'
        },
        {
            fieldName: 'ticketsTitle',
            label: 'Tickets Section Title',
            type: 'input',
            placeholder: 'Tickets',
            fontField: 'ticketsTitleFont',
            fontType: 'big',
            effectField: 'ticketsTitleEffect',
            showToggleField: 'showTicketsTitle'
        },
        {
            fieldName: 'availabilityTitle',
            label: 'Availability Label',
            type: 'input',
            placeholder: 'Availability',
            fontField: 'availabilityTitleFont',
            fontType: 'small',
            effectField: 'availabilityTitleEffect',
            showToggleField: 'showAvailabilityTitle'
        },
        {
            fieldName: 'includesTitle',
            label: 'Includes Label',
            type: 'input',
            placeholder: 'INCLUDES',
            fontField: 'includesTitleFont',
            fontType: 'small',
            effectField: 'includesTitleEffect',
            showToggleField: 'showIncludesTitle'
        },
        
        // Button Texts
        {
            fieldName: 'freeTicketButtonText',
            label: 'Free Ticket Button',
            type: 'input',
            placeholder: 'Get Free Ticket',
            fontField: 'freeTicketButtonFont',
            fontType: 'small',
            buttonEffectField: 'freeTicketButtonEffect',
            showToggleField: 'showFreeTicketButtonText'
        },
        {
            fieldName: 'buyTicketButtonText',
            label: 'Buy Ticket Button',
            type: 'input',
            placeholder: 'Buy Ticket',
            fontField: 'buyTicketButtonFont',
            fontType: 'small',
            buttonEffectField: 'buyTicketButtonEffect',
            showToggleField: 'showBuyTicketButtonText'
        },
        
        // Status Messages
        {
            fieldName: 'availableText',
            label: 'Available Status',
            type: 'input',
            placeholder: 'Available',
            fontField: 'availableTextFont',
            fontType: 'small',
            effectField: 'availableTextEffect',
            showToggleField: 'showAvailableText'
        },
        {
            fieldName: 'halfSoldText',
            label: 'Half Sold Status',
            type: 'input',
            placeholder: 'Half Sold',
            fontField: 'halfSoldTextFont',
            fontType: 'small',
            effectField: 'halfSoldTextEffect',
            showToggleField: 'showHalfSoldText'
        },
        {
            fieldName: 'sellingFastText',
            label: 'Selling Fast Status',
            type: 'input',
            placeholder: 'Selling Fast!',
            fontField: 'sellingFastTextFont',
            fontType: 'small',
            effectField: 'sellingFastTextEffect',
            showToggleField: 'showSellingFastText'
        },
        {
            fieldName: 'almostSoldOutText',
            label: 'Almost Sold Out Status',
            type: 'input',
            placeholder: 'Almost Sold Out!',
            fontField: 'almostSoldOutTextFont',
            fontType: 'small',
            effectField: 'almostSoldOutTextEffect',
            showToggleField: 'showAlmostSoldOutText'
        },
        {
            fieldName: 'limitedText',
            label: 'Limited Status',
            type: 'input',
            placeholder: 'Limited',
            fontField: 'limitedTextFont',
            fontType: 'small',
            effectField: 'limitedTextEffect',
            showToggleField: 'showLimitedText'
        }
    ];

    // Color Configuration (structured like ProductInfoWidget)
    const colorConfig = [
        [
            { fieldName: 'titleColor', label: 'Title', defaultValue: '#1e293b', icon: '/img/text-fat.svg' },
            { fieldName: 'descriptionColor', label: 'Description', defaultValue: '#64748b', icon: '/img/text.svg' },
            { fieldName: 'eventTypeColor', label: 'Event Type', defaultValue: '#f59e0b', icon: '/img/text-fat.svg' }
        ],
        [
            { fieldName: 'dateColor', label: 'Date', defaultValue: '#6366f1', icon: '/img/text.svg' },
            { fieldName: 'locationColor', label: 'Location', defaultValue: '#64748b', icon: '/img/text.svg' },
            { fieldName: 'ticketPriceColor', label: 'Ticket Price', defaultValue: '#059669', icon: '/img/text-fat.svg' }
        ],
        [
            { fieldName: 'featuresIconColor', label: 'Features Icons', defaultValue: '#f59e0b', icon: '/img/background.svg' },
            { fieldName: 'backgroundColor', label: 'Background', defaultValue: '#ffffff', icon: '/img/background.svg' }
        ],
        [
            { fieldName: 'buttonColor', label: 'Button', defaultValue: '#f59e0b', icon: '/img/background.svg' },
            { fieldName: 'buttonTextColor', label: 'Button Text', defaultValue: '#ffffff', icon: '/img/text.svg' },
            { fieldName: 'heroOverlayColor', label: 'Hero Overlay', defaultValue: 'rgba(0, 0, 0, 0.4)', icon: '/img/background.svg' }
        ]
    ];

    // Font Configuration (like ProductInfoWidget)
    const fontConfig = [
        { 
            label: 'Title', 
            sizeField: 'titleSize', 
            weightField: 'titleWeight', 
            defaultSize: '32', 
            defaultWeight: '700',
            fontField: 'titleFont',
            fontType: 'big',
            effectField: 'titleEffect'
        },
        { 
            label: 'Description', 
            sizeField: 'descriptionSize', 
            weightField: 'descriptionWeight', 
            defaultSize: '16', 
            defaultWeight: '400',
            fontField: 'descriptionFont',
            fontType: 'small',
            effectField: 'descriptionEffect'
        },
        { 
            label: 'Event Type', 
            sizeField: 'eventTypeSize', 
            weightField: 'eventTypeWeight', 
            defaultSize: '14', 
            defaultWeight: '600',
            fontField: 'eventTypeFont',
            fontType: 'small'
        },
        { 
            label: 'Date', 
            sizeField: 'dateSize', 
            weightField: 'dateWeight', 
            defaultSize: '16', 
            defaultWeight: '600',
            fontField: 'dateFont',
            fontType: 'small'
        },
        { 
            label: 'Ticket Price', 
            sizeField: 'ticketPriceSize', 
            weightField: 'ticketPriceWeight', 
            defaultSize: '24', 
            defaultWeight: '700',
            fontField: 'ticketPriceFont',
            fontType: 'big'
        }
    ];

    // 🚀 ADVANCED SETTINGS - Event Information Widget (19 Settings)
    const advancedConfig = [
        // ⬌ Dimensions (3 Settings)
        {
            fieldName: 'height',
            label: 'Widget Height',
            type: 'number',
            defaultValue: 600,
            min: 400,
            max: 1000,
            step: 10,
            unit: 'px',
            icon: '/img/element.svg'
        },
        {
            fieldName: 'eventImageWidth',
            label: 'Event Image Width',
            type: 'number',
            defaultValue: 300,
            min: 200,
            max: 500,
            step: 10,
            unit: 'px',
            icon: '/img/element.svg'
        },
        {
            fieldName: 'eventImageHeight',
            label: 'Event Image Height',
            type: 'number',
            defaultValue: 200,
            min: 150,
            max: 400,
            step: 10,
            unit: 'px',
            icon: '/img/element.svg'
        },

        // ↔ Spacing (4 Settings)
        {
            fieldName: 'marginTop',
            label: 'Space Top',
            type: 'number',
            defaultValue: 0,
            min: 0,
            max: 100,
            step: 5,
            unit: 'px',
            icon: '/img/element.svg'
        },
        {
            fieldName: 'marginBottom',
            label: 'Space Bottom',
            type: 'number',
            defaultValue: 40,
            min: 0,
            max: 100,
            step: 5,
            unit: 'px',
            icon: '/img/element.svg'
        },
        {
            fieldName: 'padding',
            label: 'Widget Padding',
            type: 'number',
            defaultValue: 40,
            min: 20,
            max: 80,
            step: 5,
            unit: 'px',
            icon: '/img/element.svg'
        },
        {
            fieldName: 'backgroundImageOpacity',
            label: 'Background Opacity',
            type: 'slider',
            defaultValue: 85,
            min: 10,
            max: 100,
            step: 5,
            unit: '%',
            color: '#f59e0b',
            description: 'Background image transparency'
        },

        // ⚙️ Behavior (10 Toggles)
        {
            fieldName: 'showTitle',
            label: 'Show Title',
            type: 'toggle',
            defaultValue: true,
            icon: '/img/text-fat.svg'
        },
        {
            fieldName: 'showDescription',
            label: 'Show Description',
            type: 'toggle',
            defaultValue: true,
            icon: '/img/text.svg'
        },
        {
            fieldName: 'showEventType',
            label: 'Show Event Type',
            type: 'toggle',
            defaultValue: true,
            icon: '/img/background.svg'
        },
        {
            fieldName: 'showDate',
            label: 'Show Date',
            type: 'toggle',
            defaultValue: true,
            icon: '/img/text.svg'
        },
        {
            fieldName: 'showLocation',
            label: 'Show Location',
            type: 'toggle',
            defaultValue: true,
            icon: '/img/background.svg'
        },
        {
            fieldName: 'showTickets',
            label: 'Show Tickets',
            type: 'toggle',
            defaultValue: true,
            icon: '/img/background.svg'
        },
        {
            fieldName: 'showCustomFeatures',
            label: 'Show Features',
            type: 'toggle',
            defaultValue: true,
            icon: '/img/background.svg'
        },
        {
            fieldName: 'showButton',
            label: 'Show Button',
            type: 'toggle',
            defaultValue: true,
            icon: '/img/background.svg'
        },
        {
            fieldName: 'showEventImage',
            label: 'Show Event Image',
            type: 'toggle',
            defaultValue: true,
            icon: '/img/background.svg'
        },
        {
            fieldName: 'showEventDays',
            label: 'Show Event Schedule',
            type: 'toggle',
            defaultValue: true,
            icon: '/img/background.svg'
        },

        // 👁 Display (2 Settings)
        {
            fieldName: 'borderRadius',
            label: 'Border Radius',
            type: 'number',
            defaultValue: 16,
            min: 0,
            max: 30,
            step: 2,
            unit: 'px',
            icon: '/img/element.svg'
        },
        {
            fieldName: 'featuresLayout',
            label: 'Features Layout',
            type: 'select',
            defaultValue: 'grid',
            options: [
                { value: 'grid', label: 'Grid Layout (Multiple Columns)' },
                { value: 'column', label: 'Single Column (All Stacked)' }
            ],
            icon: '/img/element.svg'
        }
    ];

    // Layout Configuration - Leer nach Advanced Settings Migration
    const layoutConfig = [];

    return (
        <div>
            <div className="top-widget-edit-container">
                
                {/* Event Selection */}
                <div className="widget-trenner">
                    <h3 style={{ marginBottom: '10px' }}>Event Connection</h3>
                </div>

                <div className="input-group">
                    <select 
                        name="eventId" 
                        value={editData.eventId || ''} 
                        onChange={(e) => handleEventSelection(e.target.value)}
                        className="widgetInputField"
                    >
                        <option value="">Select Event...</option>
                        {eventsLoading ? (
                            <option disabled>Loading events...</option>
                        ) : events.length === 0 ? (
                            <option disabled>No events found - Create an event in Sales Platform first</option>
                        ) : (
                            events.map(event => (
                                <option key={event.id} value={event.id}>
                                    {event.contentData?.eventType === 'digital' ? '💻' : '📍'} {event.title} ({event.status})
                                </option>
                            ))
                        )}
                    </select>
                </div>

                {/* Selected Event Info */}
                {selectedEvent && (
                    <div style={{
                        marginTop: '12px',
                        padding: '12px',
                        background: '#fef3c7',
                        borderRadius: '8px',
                        border: '1px solid #f59e0b'
                    }}>
                        <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '8px', 
                            marginBottom: '8px',
                            fontSize: '13px',
                            fontWeight: '600',
                            color: '#92400e'
                        }}>
                            <span>🎟️</span>
                            Event Connected: {selectedEvent.title}
                        </div>
                        <div style={{ fontSize: '12px', color: '#b45309', lineHeight: '1.4' }}>
                            Type: {selectedEvent.contentData?.eventType || 'Not specified'} • 
                            Days: {selectedEvent.contentData?.eventDays?.length || 0} • 
                            Tickets: {selectedEvent.contentData?.tickets?.length || 0} • 
                            Status: {selectedEvent.status}
                        </div>
                    </div>
                )}


                {/* 🎫 EVENT IMAGE CONTAINER - New Image System */}
                <ImageContainer
                    label="Event Image"
                            fieldName="eventImage"
                            currentImage={editData.eventImage}
                            onUpload={uploadImage}
                            isUploading={isUploadingEvent}
                            setIsUploading={setIsUploadingEvent}
                    showToggle={true}
                    toggleLabel="Show Event Image"
                    toggleFieldName="showEventImage"
                    toggleChecked={editData.showEventImage !== false}
                    onToggleChange={handleInputChange}
                    controls={[
                        {
                            type: 'size',
                            label: 'Image Width',
                            fieldName: 'eventImageWidth',
                            defaultValue: 300,
                            min: 200,
                            max: 500,
                            step: 10,
                            unit: 'px',
                            description: 'Adjust event image width'
                        },
                        {
                            type: 'size',
                            label: 'Image Height',
                            fieldName: 'eventImageHeight',
                            defaultValue: 200,
                            min: 150,
                            max: 400,
                            step: 10,
                            unit: 'px',
                            description: 'Adjust event image height'
                        },
                        {
                            type: 'size',
                            label: 'Border Radius',
                            fieldName: 'eventImageBorderRadius',
                            defaultValue: 12,
                            min: 0,
                            max: 30,
                            step: 2,
                            unit: 'px',
                            description: 'Event image corner roundness'
                        }
                    ]}
                    editData={editData}
                    handleInputChange={handleInputChange}
                />

                {/* 🖼️ BACKGROUND IMAGE CONTAINER - New Image System with Effects */}
                <ImageContainer
                    label="Background Image"
                    fieldName="backgroundImage"
                    currentImage={editData.backgroundImage}
                    onUpload={uploadImage}
                    isUploading={isUploadingBackground}
                    setIsUploading={setIsUploadingBackground}
                    controls={[
                        {
                            type: 'slider',
                            label: 'Background Opacity',
                            fieldName: 'backgroundImageOpacity',
                            defaultValue: 85,
                            min: 10,
                            max: 100,
                            step: 5,
                            unit: '%',
                            description: 'Background image transparency',
                            convertValue: true,
                            color: '#8b5cf6',
                            icon: '/img/background.svg'
                        },
                        {
                            type: 'image-effect',
                            label: 'Image Effect',
                            fieldName: 'imageEffect',
                            defaultValue: 'none'
                        }
                    ]}
                    editData={editData}
                    handleInputChange={handleInputChange}
                />





                {/* Premium Ticket Styles Configuration */}
                {selectedEvent && selectedEvent.contentData?.tickets?.length > 0 && (
                    <div className="ticket-styles-editor">
                        <div className="widget-trenner">
                            <h3>Ticket Styles</h3>
                        </div>
                        
                        <div style={{ marginBottom: '16px', fontSize: '13px', color: '#64748b' }}>
                            Customize the visual style for each ticket type
                        </div>
                        
                        {selectedEvent.contentData.tickets.slice(0, editData.maxTicketsToShow || 4).map((ticket, index) => (
                            <div key={index} className="input-group">
                                <label>Ticket {index + 1}: {ticket.name}</label>
                                <select
                                    value={(editData.ticketStyles && editData.ticketStyles[index]) || 'standard'}
                                    onChange={(e) => {
                                        const newTicketStyles = { ...(editData.ticketStyles || {}) };
                                        newTicketStyles[index] = e.target.value;
                                        handleInputChange({ target: { name: 'ticketStyles', value: newTicketStyles } });
                                    }}
                                    className="widgetInputField"
                                >
                                    <option value="standard">🎫 Standard Ticket</option>
                                    <option value="golden">👑 Golden Ticket</option>
                                    <option value="silver">🥈 Silver Ticket</option>
                                    <option value="premium">💎 Premium Ticket</option>
                                    <option value="vip">⭐ VIP Ticket</option>
                                </select>
                                
                                {/* Style Preview */}
                                <div style={{
                                    marginTop: '8px',
                                    padding: '8px 12px',
                                    borderRadius: '6px',
                                    fontSize: '11px',
                                    fontWeight: '600',
                                    textAlign: 'center',
                                    ...(function() {
                                        const styleType = (editData.ticketStyles && editData.ticketStyles[index]) || 'standard';
                                        switch(styleType) {
                                            case 'golden': return { 
                                                background: 'linear-gradient(135deg, #ffd700, #f59e0b)', 
                                                color: '#92400e',
                                                border: '1px solid #d97706'
                                            };
                                            case 'silver': return { 
                                                background: 'linear-gradient(135deg, #e5e7eb, #d1d5db)', 
                                                color: '#374151',
                                                border: '1px solid #9ca3af'
                                            };
                                            case 'premium': return { 
                                                background: 'linear-gradient(135deg, #8b5cf6, #9333ea)', 
                                                color: '#ffffff',
                                                border: '1px solid #7c3aed'
                                            };
                                            case 'vip': return { 
                                                background: 'linear-gradient(135deg, #dc2626, #ef4444)', 
                                                color: '#ffffff',
                                                border: '1px solid #b91c1c'
                                            };
                                            default: return { 
                                                background: 'rgba(0, 0, 0, 0.05)', 
                                                color: '#64748b',
                                                border: '1px solid rgba(0, 0, 0, 0.1)'
                                            };
                                        }
                                    })()
                                }}>
                                    {(function() {
                                        const styleType = (editData.ticketStyles && editData.ticketStyles[index]) || 'standard';
                                        switch(styleType) {
                                            case 'golden': return '👑 GOLDEN TICKET PREVIEW';
                                            case 'silver': return '🥈 SILVER TICKET PREVIEW';
                                            case 'premium': return '💎 PREMIUM TICKET PREVIEW';
                                            case 'vip': return '⭐ VIP TICKET PREVIEW';
                                            default: return '🎫 STANDARD TICKET PREVIEW';
                                        }
                                    })()}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Ticket Availability Toggle */}
                {selectedEvent && selectedEvent.contentData?.tickets?.length > 0 && (
                    <div className="input-group">
                        <ToggleSwitch
                            label="Show Ticket Availability"
                            fieldName="showTicketAvailability"
                            checked={editData.showTicketAvailability !== false}
                            onChange={handleInputChange}
                        />
                        
                        {editData.showTicketAvailability && (
                            <div style={{
                                marginTop: '12px',
                                padding: '12px',
                                background: '#ecfdf5',
                                borderRadius: '8px',
                                border: '1px solid #10b981'
                            }}>
                                <div style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '8px', 
                                    marginBottom: '8px',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    color: '#065f46'
                                }}>
                                    <i className="fas fa-chart-bar" style={{ fontSize: '12px' }}></i>
                                    Dynamic Availability: Uses real event data
                                </div>
                                <div style={{ fontSize: '12px', color: '#047857', lineHeight: '1.4' }}>
                                    Availability data is automatically pulled from your event tickets in the Sales Platform. 
                                    Update ticket availability and sales numbers in the event settings.
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* 🚀 REVOLUTIONARY TEXT SETTINGS - Event Information Widget */}
                <TextSettingsSection 
                    textConfig={textConfig}
                    editData={editData}
                    handleInputChange={handleInputChange}
                    showFontSelector={true}
                    showTextEffects={true}
                    showButtonEffects={true}
                />

                {/* 🚀 ADVANCED SETTINGS - Event Information Widget */}
                <AdvancedSettingsSection 
                    advancedConfig={advancedConfig}
                    editData={editData}
                    handleInputChange={handleInputChange}
                    handleSliderChange={handleSliderChange}
                />

                {/* 🎫 EVENT FEATURES MANAGEMENT - New Toggle Section */}
                {editData.showCustomFeatures && (
                    <EventFeaturesManagement 
                        features={editData.customFeatures || []}
                        addFeature={addFeature}
                        removeFeature={removeFeature}
                        updateFeature={updateFeature}
                        editData={editData}
                    />
                )}

                {/* Payment Integration Section */}
                <div className="widget-trenner" style={{ marginTop: '30px' }}>
                    <h3>💳 Payment Options</h3>
                </div>

                <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                    background: '#f8fafc',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    marginBottom: '15px'
                }}>
                    <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '12px'
                    }}>
                        <img 
                            src="/img/stripe.svg" 
                            alt="Stripe" 
                            style={{ width: '24px', height: '24px' }} 
                        />
                        <div>
                            <div style={{ fontWeight: '600', color: '#374151', fontSize: '14px' }}>
                                Online Payment (Stripe)
                            </div>
                            <div style={{ fontSize: '12px', color: '#6b7280' }}>
                                Secure online payment before the event
                            </div>
                        </div>
                    </div>
                    <ToggleSwitch
                        label=""
                        fieldName="useStripeCheckout"
                        checked={editData.useStripeCheckout !== false}
                        onChange={handleInputChange}
                    />
                </div>

                {editData.useStripeCheckout !== false ? (
                    <div style={{
                        padding: '16px',
                        background: '#f0f9ff',
                        borderRadius: '8px',
                        border: '1px solid #0ea5e9',
                        marginBottom: '15px'
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            marginBottom: '8px'
                        }}>
                            <i className="fas fa-credit-card" style={{ color: '#0ea5e9' }}></i>
                            <span style={{ fontWeight: '600', color: '#0c4a6e', fontSize: '13px' }}>
                                Online Payment Active
                            </span>
                        </div>
                        <p style={{ 
                            fontSize: '12px', 
                            color: '#0c4a6e', 
                            margin: '0',
                            lineHeight: '1.4'
                        }}>
                            Visitors can pay online via Stripe. Configure Stripe Product ID and Price ID in your event settings. 
                            Free tickets will show a contact form instead.
                        </p>
                    </div>
                ) : (
                    <div style={{
                        padding: '16px',
                        background: '#fef3c7',
                        borderRadius: '8px',
                        border: '1px solid #f59e0b',
                        marginBottom: '15px'
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            marginBottom: '8px'
                        }}>
                            <i className="fas fa-map-marker-alt" style={{ color: '#d97706' }}></i>
                            <span style={{ fontWeight: '600', color: '#92400e', fontSize: '13px' }}>
                                Pay On-Site Active
                            </span>
                        </div>
                        <p style={{ 
                            fontSize: '12px', 
                            color: '#92400e', 
                            margin: '0',
                            lineHeight: '1.4'
                        }}>
                            Visitors will register via form and pay at the event location. 
                            Perfect for events where you want to handle payments in person.
                        </p>
                    </div>
                )}

                <div className="input-group">
                    <label>Custom Button Link</label>
                    <input
                        type="url"
                        name="buttonLink"
                        value={editData.buttonLink || ''}
                        onChange={handleInputChange}
                        placeholder="https://your-custom-link.com (overrides payment options)"
                        className="widgetInputField"
                    />
                    <small style={{ color: '#6b7280', fontSize: '11px' }}>
                        Optional: Custom link overrides both Stripe and on-site payment
                    </small>
                </div>


                {/* Use MoreSettingsSection like ProductInfoWidget */}
                <MoreSettingsSection
                    colors={colors}
                    colorConfig={colorConfig}
                    fontConfig={fontConfig}
                    layoutConfig={layoutConfig}
                    editData={editData}
                    handleInputChange={handleInputChange}
                    showFontSelector={true}
                    showTextEffects={true}
                    showButtonEffects={true}
                    showImageEffects={editData.backgroundImage ? true : false}
                    showLayoutOptions={true}
                    showResponsiveOptions={true}
                />
            </div>
        </div>
    );
}

export default EventInfoWidgetEdit;
